import React from 'react';
import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';

import {
  selectClimateMinMax,
  selectProjectedChartData,
  selectClimateCsvData
} from '../../../store/data/data.selector';
import {
  selectAttributeId,
  selectAttributeInfo,
  selectDateRange
} from '../../../store/options/options.selector';
import { selectCurrentLocation, selectCurrentStation, selectUseStationData } from '../../../store/map/map.selector';
import { selectChartTab } from '../../../store/ui/ui.selector';

import downloadData from '../../../utilities/downloadData';

import { getAttributeText } from '../../../utilities/mapPopupText';

export default function ProjectedChart({
  HighchartsReact,
  Highcharts,
  defaultOptions,
}) {
  const data = useSelector(selectProjectedChartData);
  const csvData = useSelector(selectClimateCsvData);
  const attr = useSelector(selectAttributeId);
  const loc = useSelector(selectCurrentLocation);
  const attrInfo = useSelector(selectAttributeInfo);
  const minMax = useSelector(selectClimateMinMax);
  const tab = useSelector(selectChartTab);
  const useStation = useSelector(selectUseStationData);
  const currentStation = useSelector(selectCurrentStation);
  const dateRange = useSelector(selectDateRange);

  let { attrName, unit, popupUnit } = getAttributeText(true, attr);

  let titleText;
  if (useStation) {
    titleText = `${attrName} at ${currentStation.name} from ${new Date().getFullYear()} to 2099 under a ${tab.charAt(0).toUpperCase() + tab.slice(1)} Emissions Future Scenario (Model Data)`;
  } else {
    titleText = `${attrName} in ${loc.state ? `${loc.name} County, ${loc.state}` : loc.name} from ${new Date().getFullYear()} to 2099 under a ${tab.charAt(0).toUpperCase() + tab.slice(1)} Emissions Future Scenario (Model Data)`;
  }

  const options = {
    ...defaultOptions,
    title: {
      ...defaultOptions.title,
      text: titleText
    },
    exporting: {
      ...defaultOptions.exporting,
      chartOptions: {
        subtitle: '',
      },
      menuItemDefinitions: {
        downloadData: {
          onclick: () => downloadData(
            'climate',
            loc,
            attrInfo,
            csvData,
            dateRange
          ),
          text: 'Download Chart Data',
        },
      },
    },
    series: [
      {
        marker: {
          enabled: false,
        },
        type: 'arearange',
        data: data.range,
        name: 'Climate Model Range',
      },
      {
        data: data.mean,
        name: 'Climate Model Average',
      },
    ],
    yAxis: {
      gridLineColor: '#a1a1a1',
      title: {
        text:
          unit && unit !== ''
            ? unit.slice(0, 1).toUpperCase() + unit.slice(1)
            : 'USD',
      },
      min: minMax.min,
      max: minMax.max,
      gridLineWidth: data.mean.length ? 1 : 0,
    },
    tooltip: {
      crosshairs: true,
      shared: true,
      formatter: function () {
        return this.points.reduce(function (s, point) {
          let name = point.series.name.split(' ')[2];
          return (
            s +
            '<br/>' +
            name +
            ': ' +
            `${
              point.point.high !== undefined && point.point.low !== undefined
                ? `<b>${point.point.low.toFixed(
                    2
                  )}</b>-<b>${point.point.high.toFixed(2)}</b>`
                : `<b>${point.y.toFixed(2)}</b>`
            } ${popupUnit}`
          );
        }, '<b>' + this.x + ' Climate Model</b>');
      },
    },
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      containerProps={{ style: { height: '100%', width: '100%' } }}
    />
  );
}

ProjectedChart.propTypes = {
  HighchartsReact: PropTypes.object,
  Highcharts: PropTypes.object,
  defaultOptions: PropTypes.object,
};
